import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useInstitution } from "../contexts/InstitutionContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Dot,
} from "recharts";
import {
  SCORE_CATEGORY_PRESETS,
  getMetricConfigForCategory,
  normalizeScoreValue,
  collectMetricConfigsFromSessions,
} from "../utils/scoreMetrics.jsx";
import {
  MessageCircle,
  Users,
  Zap,
  Target,
  Lightbulb,
  TrendingUp,
  Calendar,
  ArrowLeft,
  Filter,
  X,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  UserCircle,
  Star,
  AlertTriangle,
  Download,
} from "lucide-react";
import { downloadTutorAggregateCsv } from "../utils/sessionsCsvExport";

const TrendlineView = () => {
  const { sessions, institutionId, basePath } = useInstitution();
  const navigate = useNavigate();
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [selectedMetrics, setSelectedMetrics] = useState({});

  // Time period filtering
  const [dateFilter, setDateFilter] = useState("all"); // all, 7d, 30d, 90d, custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // User filtering
  const [selectedTeacher, setSelectedTeacher] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState("all");

  // Selected session for details view
  const [selectedSession, setSelectedSession] = useState(null);

  // Show average lines toggle
  const [showAverageLines, setShowAverageLines] = useState(true);

  // Show tutor analysis table
  const [showTutorAnalysis, setShowTutorAnalysis] = useState(true);

  // Tutor analysis table state
  const [tutorSearchQuery, setTutorSearchQuery] = useState("");
  const [tutorSortField, setTutorSortField] = useState("sessionCount");
  const [tutorSortDirection, setTutorSortDirection] = useState("desc");
  const [tutorCurrentPage, setTutorCurrentPage] = useState(1);
  const tutorRowsPerPage = 10;

  const studentRoles = ["Student", "Guest", "Parent", "Anonymous"];

  // Get unique teachers and students from all sessions
  const getUserLists = (allSessions) => {
    const teachersMap = new Map();
    const studentsMap = new Map();

    if (!allSessions || allSessions.length === 0) {
      return { teachers: [], students: [] };
    }

    allSessions.forEach((session) => {
      const userObjects = session.user_objects || {};
      Object.entries(userObjects).forEach(([userId, user]) => {
        if (studentRoles.includes(user.persona)) {
          studentsMap.set(userId, user.name || "Unknown");
        } else {
          // All other roles are considered teachers (Teacher, Admin, etc.)
          teachersMap.set(userId, user.name || "Unknown");
        }
      });
    });

    const teachers = Array.from(teachersMap.entries()).map(([id, name]) => ({
      id,
      name,
    }));
    const students = Array.from(studentsMap.entries()).map(([id, name]) => ({
      id,
      name,
    }));

    // Sort by name
    teachers.sort((a, b) => a.name.localeCompare(b.name));
    students.sort((a, b) => a.name.localeCompare(b.name));

    return { teachers, students };
  };

  const { teachers, students } = getUserLists(sessions);

  // Filter sessions by date range and users
  const getFilteredSessions = (allSessions) => {
    if (!allSessions || allSessions.length === 0) return [];

    const now = new Date();
    let startDate = null;

    switch (dateFilter) {
      case "7d":
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30d":
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case "90d":
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case "custom":
        if (customStartDate) {
          startDate = new Date(customStartDate);
        }
        break;
      default:
        // "all" - no filtering by date
        break;
    }

    let filtered = allSessions;

    // Apply start date filter
    if (startDate) {
      filtered = filtered.filter((session) => {
        const sessionDate = new Date(session.recording_object?.startingTime);
        return sessionDate >= startDate;
      });
    }

    // Apply end date filter for custom range
    if (dateFilter === "custom" && customEndDate) {
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999); // Include entire end day
      filtered = filtered.filter((session) => {
        const sessionDate = new Date(session.recording_object?.startingTime);
        return sessionDate <= endDate;
      });
    }

    // Apply teacher filter
    if (selectedTeacher !== "all") {
      filtered = filtered.filter((session) => {
        const userObjects = session.user_objects || {};
        return Object.keys(userObjects).includes(selectedTeacher);
      });
    }

    // Apply student filter
    if (selectedStudent !== "all") {
      filtered = filtered.filter((session) => {
        const userObjects = session.user_objects || {};
        return Object.keys(userObjects).includes(selectedStudent);
      });
    }

    return filtered;
  };

  const filteredSessionsForMetrics = useMemo(
    () => getFilteredSessions(sessions),
    [
      sessions,
      dateFilter,
      customStartDate,
      customEndDate,
      selectedTeacher,
      selectedStudent,
    ]
  );

  const baseMetricConfigs = useMemo(
    () => collectMetricConfigsFromSessions(filteredSessionsForMetrics),
    [filteredSessionsForMetrics]
  );

  const overallConfig = SCORE_CATEGORY_PRESETS.overall;

  const chartLineMetricConfigs = useMemo(
    () => [
      overallConfig,
      ...baseMetricConfigs.filter((c) => c.key !== overallConfig.key),
    ],
    [baseMetricConfigs, overallConfig]
  );

  useEffect(() => {
    setSelectedMetrics((prev) => {
      const next = { ...prev };
      let changed = false;
      chartLineMetricConfigs.forEach((cfg) => {
        if (next[cfg.key] === undefined) {
          next[cfg.key] = cfg.key === overallConfig.key;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [chartLineMetricConfigs, overallConfig.key]);

  // Process sessions into chart data
  const chartData = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    // Filter sessions by date range first
    const filteredSessions = getFilteredSessions(sessions);

    // Sort sessions by date
    const sortedSessions = [...filteredSessions].sort((a, b) => {
      const dateA = new Date(a.recording_object?.startingTime || 0);
      const dateB = new Date(b.recording_object?.startingTime || 0);
      return dateA - dateB;
    });

    return sortedSessions.map((session) => {
      const dataPoint = {
        sessionId: session.recording_id,
        sessionName: session.space_name,
        date: new Date(
          session.recording_object?.startingTime
        ).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        timestamp: session.recording_object?.startingTime,
      };

      const applyScoring = (scoreItem) => {
        const config = getMetricConfigForCategory(scoreItem.skillCategory);
        if (!config) {
          return;
        }
        const normalizedScore = normalizeScoreValue(scoreItem.score);
        if (normalizedScore === null) {
          return;
        }
        dataPoint[config.key] = normalizedScore;
        dataPoint[`${config.key}_reason`] =
          scoreItem.reason || "No details available";
        if (scoreItem.level) {
          dataPoint[`${config.key}_level`] = scoreItem.level;
        }
      };

      const softSkillsScoring =
        session.soft_skills_evaluation_2?.scoring || session.scoring || [];
      softSkillsScoring.forEach(applyScoring);

      const scores = baseMetricConfigs
        .map((cfg) => dataPoint[cfg.key])
        .filter((score) => score !== undefined);

      if (scores.length > 0) {
        dataPoint.overallScore = Math.round(
          scores.reduce((sum, score) => sum + score, 0) / scores.length
        );
        dataPoint.overallScore_reason = `Overall score calculated from ${scores.length} metrics`;
      }

      return dataPoint;
    });
  }, [
    sessions,
    dateFilter,
    customStartDate,
    customEndDate,
    selectedTeacher,
    selectedStudent,
    baseMetricConfigs,
  ]);

  // Calculate statistics and average lines
  const statistics = useMemo(() => {
    if (chartData.length === 0) return null;

    const stats = {};
    chartLineMetricConfigs.forEach((config) => {
      const values = chartData
        .map((d) => d[config.key])
        .filter((v) => v !== undefined);

      if (values.length > 0) {
        const average = values.reduce((a, b) => a + b, 0) / values.length;
        const trend =
          values.length > 1 ? values[values.length - 1] - values[0] : 0;

        stats[config.key] = {
          average: Math.round(average),
          trend: Math.round(trend),
          latest: values[values.length - 1],
          count: values.length,
        };
      }
    });

    return stats;
  }, [chartData, chartLineMetricConfigs]);

  // Add average lines to chart data
  const chartDataWithAverages = useMemo(() => {
    if (!chartData || chartData.length === 0 || !statistics) return chartData;

    return chartData.map((dataPoint) => {
      const enrichedPoint = { ...dataPoint };

      chartLineMetricConfigs.forEach((config) => {
        if (statistics[config.key]) {
          enrichedPoint[`${config.key}_avg`] = statistics[config.key].average;
        }
      });

      return enrichedPoint;
    });
  }, [chartData, statistics, chartLineMetricConfigs]);

  const visibleChartLineConfigs = useMemo(
    () =>
      chartLineMetricConfigs.filter((cfg) =>
        chartData.some((d) => d[cfg.key] !== undefined)
      ),
    [chartData, chartLineMetricConfigs]
  );

  // Aggregate data by teacher for tutor analysis
  const tutorAnalysisData = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    const filteredSessions = getFilteredSessions(sessions);
    const teacherStats = new Map();

    filteredSessions.forEach((session) => {
      const userObjects = session.user_objects || {};

      Object.entries(userObjects).forEach(([userId, user]) => {
        if (studentRoles.includes(user.persona)) {
          return;
        }

        // Exclude unknown speakers from tutor analysis
        if (!user.persona || user.persona === "Unknown" || !user.name) {
          return;
        }

        if (!teacherStats.has(userId)) {
          teacherStats.set(userId, {
            teacherId: userId,
            teacherName: user.name || "Unknown",
            sessions: [],
            scores: {},
          });
        }

        const stats = teacherStats.get(userId);
        stats.sessions.push(session.recording_id);

        const softSkillsScoring =
          session.soft_skills_evaluation_2?.scoring || session.scoring || [];

        const sessionVals = [];
        softSkillsScoring.forEach((scoreItem) => {
          const config = getMetricConfigForCategory(scoreItem.skillCategory);
          if (!config) {
            return;
          }
          const normalizedScore = normalizeScoreValue(scoreItem.score);
          if (normalizedScore === null) {
            return;
          }
          if (!stats.scores[config.key]) {
            stats.scores[config.key] = [];
          }
          stats.scores[config.key].push(normalizedScore);
          sessionVals.push(normalizedScore);
        });

        if (sessionVals.length > 0) {
          if (!stats.scores.overallScore) {
            stats.scores.overallScore = [];
          }
          stats.scores.overallScore.push(
            Math.round(
              sessionVals.reduce((sum, score) => sum + score, 0) /
                sessionVals.length
            )
          );
        }
      });
    });

    const metricKeys = new Set(["overallScore"]);
    baseMetricConfigs.forEach((c) => metricKeys.add(c.key));

    return Array.from(teacherStats.values()).map((stats) => {
      const result = {
        teacherId: stats.teacherId,
        teacherName: stats.teacherName,
        sessionCount: new Set(stats.sessions).size,
      };

      metricKeys.forEach((key) => {
        const scores = stats.scores[key];
        if (scores && scores.length > 0) {
          result[key] = Math.round(
            scores.reduce((a, b) => a + b, 0) / scores.length
          );
        } else {
          result[key] = null;
        }
      });

      return result;
    });
  }, [
    sessions,
    dateFilter,
    customStartDate,
    customEndDate,
    selectedTeacher,
    selectedStudent,
    baseMetricConfigs,
  ]);

  const tutorColumnConfigs = useMemo(() => {
    if (!tutorAnalysisData.length) return [];
    const keySet = new Set();
    tutorAnalysisData.forEach((row) => {
      Object.entries(row).forEach(([k, v]) => {
        if (["teacherId", "teacherName", "sessionCount"].includes(k)) {
          return;
        }
        if (v !== null && v !== undefined) {
          keySet.add(k);
        }
      });
    });
    const cfgByKey = new Map(chartLineMetricConfigs.map((c) => [c.key, c]));
    const out = [];
    if (keySet.has(overallConfig.key)) {
      out.push(cfgByKey.get(overallConfig.key) || overallConfig);
    }
    Array.from(keySet)
      .filter((k) => k !== overallConfig.key)
      .sort((a, b) => {
        const la = cfgByKey.get(a)?.fullLabel || a;
        const lb = cfgByKey.get(b)?.fullLabel || b;
        return la.localeCompare(lb);
      })
      .forEach((k) => {
        const existing = cfgByKey.get(k);
        if (existing) {
          out.push(existing);
        } else {
          out.push({
            key: k,
            label: k
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (s) => s.toUpperCase())
              .trim(),
            fullLabel: k.replace(/([A-Z])/g, " $1").trim(),
            color: "#6B7280",
            icon: <Target className="w-4 h-4" />,
          });
        }
      });
    return out;
  }, [tutorAnalysisData, chartLineMetricConfigs, overallConfig]);

  // Filter, sort, and paginate tutor data
  const processedTutorData = useMemo(() => {
    let data = [...tutorAnalysisData];

    // Filter by search query
    if (tutorSearchQuery.trim()) {
      const query = tutorSearchQuery.toLowerCase();
      data = data.filter((tutor) =>
        tutor.teacherName.toLowerCase().includes(query)
      );
    }

    // Sort
    data.sort((a, b) => {
      let aVal = a[tutorSortField];
      let bVal = b[tutorSortField];

      // Handle null values
      if (aVal === null) aVal = -1;
      if (bVal === null) bVal = -1;

      if (tutorSortField === "teacherName") {
        // String comparison
        return tutorSortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        // Number comparison
        return tutorSortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
    });

    return data;
  }, [tutorAnalysisData, tutorSearchQuery, tutorSortField, tutorSortDirection]);

  // Paginate tutor data
  const paginatedTutorData = useMemo(() => {
    const startIndex = (tutorCurrentPage - 1) * tutorRowsPerPage;
    const endIndex = startIndex + tutorRowsPerPage;
    return processedTutorData.slice(startIndex, endIndex);
  }, [processedTutorData, tutorCurrentPage]);

  const totalTutorPages = Math.ceil(
    processedTutorData.length / tutorRowsPerPage
  );

  const highestScores = useMemo(() => {
    const getMax = (arr, key) => {
      const validScores = arr
        .map((t) => t[key])
        .filter((score) => score !== null && score !== undefined);
      return validScores.length > 0 ? Math.max(...validScores) : null;
    };

    const out = {};
    tutorColumnConfigs.forEach((cfg) => {
      out[cfg.key] = getMax(tutorAnalysisData, cfg.key);
    });
    return out;
  }, [tutorAnalysisData, tutorColumnConfigs]);

  // Handle sort
  const handleTutorSort = (field) => {
    if (tutorSortField === field) {
      setTutorSortDirection(tutorSortDirection === "asc" ? "desc" : "asc");
    } else {
      setTutorSortField(field);
      setTutorSortDirection("desc");
    }
    setTutorCurrentPage(1); // Reset to first page on sort
  };

  // Handle data point click
  const handlePointClick = (data) => {
    // Try different ways to get the session data
    let sessionData = null;

    if (data && data.activePayload && data.activePayload.length > 0) {
      sessionData = data.activePayload[0].payload;
    } else if (
      data &&
      data.activeTooltipIndex !== undefined &&
      chartData[data.activeTooltipIndex]
    ) {
      sessionData = chartData[data.activeTooltipIndex];
    } else if (data && data.activeLabel) {
      // Find by date label
      sessionData = chartData.find((d) => d.date === data.activeLabel);
    }

    if (sessionData) {
      setSelectedSession(sessionData);
      // Scroll to details section
      setTimeout(() => {
        const element = document.getElementById("session-details");
        element?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  // Custom dot component for interactive points
  const CustomDot = (props) => {
    const { cx, cy, payload, dataKey, onClick } = props;
    const config = chartLineMetricConfigs.find((c) => c.key === dataKey);

    if (!config || selectedMetrics[dataKey] !== true) return null;

    const isHovered =
      hoveredPoint?.sessionId === payload.sessionId &&
      hoveredPoint?.metric === dataKey;

    const isSelected = selectedSession?.sessionId === payload.sessionId;

    const metricIndex = visibleChartLineConfigs.findIndex(
      (c) => c.key === dataKey
    );
    const yOffset = (metricIndex >= 0 ? metricIndex : 0) * 2;

    const handleDotClick = (e) => {
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
      setSelectedSession(payload);
      // Scroll to details section
      setTimeout(() => {
        const element = document.getElementById("session-details");
        element?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    };

    const dotRadius = isSelected ? 8 : isHovered ? 7 : 5;

    return (
      <g>
        {/* Larger invisible hit area for easier clicking */}
        <circle
          cx={cx}
          cy={cy + yOffset}
          r={12}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onClick={handleDotClick}
          onMouseEnter={() =>
            setHoveredPoint({ sessionId: payload.sessionId, metric: dataKey })
          }
          onMouseLeave={() => setHoveredPoint(null)}
        />
        {/* Visible dot */}
        <Dot
          cx={cx}
          cy={cy + yOffset}
          r={dotRadius}
          fill={config.color}
          stroke={isSelected ? "#4F46E5" : "#fff"}
          strokeWidth={isSelected ? 3 : 2}
          style={{
            cursor: "pointer",
            transition: "all 0.2s",
            pointerEvents: "none", // Let the invisible circle handle events
          }}
        />
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    // Get the actual data point being hovered
    const data = payload[0].payload;

    // Filter payload to only show selected metrics and exclude average lines
    const filteredPayload = payload.filter((entry) => {
      const dataKey = entry.dataKey;
      // Skip average lines (those ending with _avg)
      if (dataKey && dataKey.endsWith("_avg")) return false;
      // Only show metrics that are currently selected
      return selectedMetrics[dataKey] === true;
    });

    if (filteredPayload.length === 0) return null;

    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg shadow-xl p-4 max-w-sm">
        <div className="font-semibold text-gray-900 mb-2">
          {data.sessionName}
        </div>
        <div className="text-sm text-gray-600 mb-3 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {data.date}
        </div>
        <div className="space-y-2">
          {filteredPayload.map((entry) => {
            const config = chartLineMetricConfigs.find(
              (c) => c.key === entry.dataKey
            );
            if (!config) return null;

            // Get the score from entry.payload which is the actual data point
            const actualData = entry.payload;
            const score = actualData[config.key];
            const reason = actualData[`${config.key}_reason`];

            return (
              <div key={entry.dataKey} className="border-t pt-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div style={{ color: config.color }}>{config.icon}</div>
                    <span className="text-sm font-medium text-gray-700">
                      {config.label}
                    </span>
                  </div>
                  <span
                    className="text-lg font-bold"
                    style={{ color: config.color }}
                  >
                    {score !== undefined ? score : entry.value}
                  </span>
                </div>
                {reason && (
                  <p className="text-xs text-gray-600 mt-1 italic line-clamp-2">
                    "{reason}"
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-3 pt-2 border-t text-xs text-gray-500 text-center">
          Click point to view detailed breakdown below
        </div>
      </div>
    );
  };

  const toggleMetric = (metricKey) => {
    setSelectedMetrics((prev) => ({
      ...prev,
      [metricKey]: prev[metricKey] !== true,
    }));
  };

  if (!sessions || sessions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600">No sessions available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(basePath)}
            className="flex items-center gap-2 text-[#2f80ed] hover:text-[#1e5bb8] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back to Sessions</span>
          </button>
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-8 h-8 text-[#2f80ed]" />
              <h1 className="text-4xl font-bold text-gray-800">
                Score Trendlines
              </h1>
            </div>
            <p className="text-gray-600 text-lg">
              Track performance metrics across all sessions
            </p>
          </div>

          {/* Filter Controls */}
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-[#2f80ed]" />
              <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
            </div>

            {/* User Filters */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Filter by Participants
              </h4>
              <div className="flex flex-wrap gap-4">
                {/* Teacher Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">
                    Teacher:
                  </label>
                  <select
                    value={selectedTeacher}
                    onChange={(e) => setSelectedTeacher(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f80ed] focus:border-transparent text-sm bg-white"
                  >
                    <option value="all">All Teachers</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Student Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">
                    Student:
                  </label>
                  <select
                    value={selectedStudent}
                    onChange={(e) => setSelectedStudent(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f80ed] focus:border-transparent text-sm bg-white"
                  >
                    <option value="all">All Students</option>
                    {students.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear User Filters Button */}
                {(selectedTeacher !== "all" || selectedStudent !== "all") && (
                  <button
                    onClick={() => {
                      setSelectedTeacher("all");
                      setSelectedStudent("all");
                    }}
                    className="px-3 py-2 text-sm text-[#2f80ed] hover:text-[#1e5bb8] hover:bg-blue-50 rounded-lg transition-colors font-medium"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Time Period Filters */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Filter by Time Period
              </h4>
              <div className="flex flex-wrap items-end gap-4">
                {/* Quick Filters */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setDateFilter("all");
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      dateFilter === "all"
                        ? "bg-[#2f80ed] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    All Time
                  </button>
                  <button
                    onClick={() => {
                      setDateFilter("7d");
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      dateFilter === "7d"
                        ? "bg-[#2f80ed] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateFilter("30d");
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      dateFilter === "30d"
                        ? "bg-[#2f80ed] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => {
                      setDateFilter("90d");
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      dateFilter === "90d"
                        ? "bg-[#2f80ed] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Last 90 Days
                  </button>
                  <button
                    onClick={() => setDateFilter("custom")}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      dateFilter === "custom"
                        ? "bg-[#2f80ed] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Custom Range
                  </button>
                </div>

                {/* Custom Date Range Inputs */}
                {dateFilter === "custom" && (
                  <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">
                        From:
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f80ed] focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">
                        To:
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f80ed] focus:border-transparent text-sm"
                      />
                    </div>
                    {(customStartDate || customEndDate) && (
                      <button
                        onClick={() => {
                          setCustomStartDate("");
                          setCustomEndDate("");
                        }}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        title="Clear custom dates"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Filter Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing{" "}
                <span className="font-semibold text-[#2f80ed]">
                  {chartData.length}
                </span>{" "}
                session{chartData.length !== 1 ? "s" : ""} out of{" "}
                <span className="font-semibold">{sessions?.length || 0}</span>{" "}
                total
                {dateFilter !== "all" && (
                  <span className="ml-2 text-gray-500">
                    (
                    {dateFilter === "custom"
                      ? `${customStartDate || "Start"} to ${
                          customEndDate || "End"
                        }`
                      : `Last ${dateFilter.replace("d", " days")}`}
                    )
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Overall Score Card - Left Side */}
              {statistics.overallScore && (
                <div
                  className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-200 hover:shadow-2xl transition-all cursor-pointer flex flex-col group"
                  onClick={() => toggleMetric("overallScore")}
                  style={{
                    opacity: selectedMetrics.overallScore === true ? 1 : 0.5,
                    borderColor:
                      selectedMetrics.overallScore === true
                        ? overallConfig.color
                        : "#e5e7eb",
                  }}
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <div
                        className="p-3 rounded-xl"
                        style={{
                          backgroundColor: overallConfig.color,
                          color: "white",
                        }}
                      >
                        <TrendingUp className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">
                          Overall Score
                        </h3>
                        <p className="text-xs text-gray-500">
                          Average across all metrics
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to view graph
                    </span>
                  </div>
                  <div className="flex flex-col items-center justify-center flex-1">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Average Score
                    </div>
                    <div
                      className="text-6xl font-bold mb-4"
                      style={{ color: overallConfig.color }}
                    >
                      {statistics.overallScore.average}
                    </div>
                    <div className="flex items-center justify-center gap-4">
                      <span
                        className={`text-sm font-medium px-3 py-1.5 rounded-full ${
                          statistics.overallScore.trend >= 0
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {statistics.overallScore.trend >= 0 ? "+" : ""}
                        {statistics.overallScore.trend} trend
                      </span>
                      <span className="text-sm text-gray-600">
                        {statistics.overallScore.count} session
                        {statistics.overallScore.count !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Individual Metric Cards - Right Side, Stacked */}
              <div className="flex flex-col gap-4">
                {visibleChartLineConfigs
                  .filter((config) => config.key !== overallConfig.key)
                  .map((config) => {
                    const stat = statistics[config.key];
                    if (!stat) return null;

                    return (
                      <div
                        key={config.key}
                        className="bg-white rounded-xl p-4 shadow-md border border-gray-100 hover:shadow-lg transition-all cursor-pointer flex items-center justify-between flex-1 group"
                        onClick={() => toggleMetric(config.key)}
                        style={{
                          opacity:
                            selectedMetrics[config.key] === true ? 1 : 0.5,
                          borderColor:
                            selectedMetrics[config.key] === true
                              ? config.color
                              : "#e5e7eb",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="p-2 rounded-lg"
                            style={{
                              backgroundColor: config.color,
                              color: "white",
                            }}
                          >
                            {config.icon}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-800">
                              {config.label}
                            </div>
                            <div className="text-xs text-gray-500">
                              {stat.count} session{stat.count !== 1 ? "s" : ""}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            Click to view
                          </span>
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col items-end">
                              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                                Average
                              </div>
                              <div
                                className="text-3xl font-bold"
                                style={{ color: config.color }}
                              >
                                {stat.average}
                              </div>
                            </div>
                            <span
                              className={`text-xs font-medium px-2 py-1 rounded ${
                                stat.trend >= 0
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {stat.trend >= 0 ? "+" : ""}
                              {stat.trend}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Chart */}
        <div
          className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-8"
          style={{ userSelect: "none" }}
        >
          <div className="mb-6">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  Performance Over Time
                </h2>
                <p className="text-gray-600">
                  Click on any data point to view detailed session analysis
                </p>
              </div>
              {showAverageLines && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <svg width="32" height="2" className="inline-block">
                        <line
                          x1="0"
                          y1="1"
                          x2="32"
                          y2="1"
                          stroke="#6366f1"
                          strokeWidth="2"
                        />
                      </svg>
                      <span className="text-gray-700">Individual Sessions</span>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <svg width="32" height="2" className="inline-block">
                        <line
                          x1="0"
                          y1="1"
                          x2="32"
                          y2="1"
                          stroke="#6366f1"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                          opacity="0.5"
                        />
                      </svg>
                      <span className="text-gray-700">Average Benchmark</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              data={chartDataWithAverages}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              onClick={handlePointClick}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                style={{ fontSize: "12px" }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: "12px" }}
                domain={[0, 100]}
                ticks={[0, 20, 40, 60, 80, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="line"
                onClick={(e) => {
                  e.stopPropagation?.();
                  toggleMetric(e.dataKey);
                }}
                formatter={(value, entry) => {
                  const config = chartLineMetricConfigs.find(
                    (c) => c.key === entry.dataKey
                  );
                  if (config) {
                    return config.fullLabel;
                  }
                  return value;
                }}
              />

              {visibleChartLineConfigs.map((config) => (
                <Line
                  key={config.key}
                  type="monotone"
                  dataKey={config.key}
                  stroke={config.color}
                  strokeWidth={selectedMetrics[config.key] === true ? 3 : 0}
                  dot={<CustomDot />}
                  activeDot={{
                    r: 8,
                    onClick: (e, payload) => {
                      if (e && e.stopPropagation) {
                        e.stopPropagation();
                      }
                      setSelectedSession(payload.payload);
                      setTimeout(() => {
                        const element =
                          document.getElementById("session-details");
                        element?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 100);
                    },
                  }}
                  connectNulls
                  hide={selectedMetrics[config.key] !== true}
                />
              ))}

              {showAverageLines &&
                visibleChartLineConfigs.map((config) => (
                  <Line
                    key={`${config.key}_avg`}
                    type="monotone"
                    dataKey={`${config.key}_avg`}
                    stroke={config.color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    strokeOpacity={0.5}
                    connectNulls
                    hide={selectedMetrics[config.key] !== true}
                    legendType="none"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>

          {/* Legend Instructions and Average Toggle */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Click on metric cards above or legend items to toggle visibility
              </p>
              <button
                onClick={() => setShowAverageLines(!showAverageLines)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showAverageLines
                    ? "bg-blue-100 text-[#1e5bb8] hover:bg-blue-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    strokeDasharray={showAverageLines ? "5,5" : "0"}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                {showAverageLines ? "Hide" : "Show"} Average Lines
              </button>
            </div>
          </div>

          {/* Selected Session Details - Inline Below Chart */}
          {selectedSession && (
            <div
              id="session-details"
              className="mt-8 pt-8 border-t-2 border-gray-200"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Session Details
                  </h3>
                  <div className="flex items-center gap-4 text-gray-600">
                    <div className="font-medium text-lg">
                      {selectedSession.sessionName}
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="w-4 h-4" />
                      {selectedSession.date}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      navigate(
                        `${basePath}/session/${selectedSession.sessionId}`
                      );
                    }}
                    className="px-4 py-2 bg-[#2f80ed] text-white rounded-lg hover:bg-[#1e5bb8] transition-colors text-sm font-medium"
                  >
                    View Full Session
                  </button>
                  <button
                    onClick={() => setSelectedSession(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Close details"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Overall Score Card - Large, Left Side */}
                {selectedSession.overallScore !== undefined && (
                  <div
                    className="bg-white border-2 rounded-2xl p-6 hover:border-gray-300 transition-colors flex flex-col"
                    style={{ borderColor: overallConfig.color }}
                  >
                    <div className="flex items-center gap-3 mb-auto">
                      <div
                        className="p-3 rounded-xl"
                        style={{
                          backgroundColor: overallConfig.color,
                          color: "white",
                        }}
                      >
                        <TrendingUp className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-800">
                          {overallConfig.fullLabel}
                        </h4>
                        <p className="text-xs text-gray-500">
                          Average across all metrics
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-center justify-center flex-1 py-8">
                      <div
                        className="text-7xl font-bold"
                        style={{ color: overallConfig.color }}
                      >
                        {selectedSession.overallScore}
                      </div>
                    </div>

                    {selectedSession.overallScore_reason && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 mt-auto">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Analysis
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {selectedSession.overallScore_reason}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Individual Metric Cards - Smaller, Right Side */}
                <div className="flex flex-col gap-4">
                  {chartLineMetricConfigs
                    .filter((config) => config.key !== overallConfig.key)
                    .map((config) => {
                      const score = selectedSession[config.key];
                      const reason = selectedSession[`${config.key}_reason`];
                      const level = selectedSession[`${config.key}_level`];

                      if (score === undefined) return null;

                      return (
                        <div
                          key={config.key}
                          className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors flex-1"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="p-2 rounded-lg"
                                style={{
                                  backgroundColor: config.color,
                                  color: "white",
                                }}
                              >
                                {config.icon}
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-800">
                                  {config.fullLabel}
                                </h4>
                                {level && (
                                  <span
                                    className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                    style={{
                                      color: config.color,
                                      backgroundColor: `${config.color}1A`,
                                    }}
                                  >
                                    {level}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              className="text-3xl font-bold"
                              style={{ color: config.color }}
                            >
                              {score}
                            </div>
                          </div>

                          {reason && (
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Analysis
                              </div>
                              <p className="text-xs text-gray-700 leading-relaxed">
                                {reason}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Navigation hint */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  Click "View Full Session" to see the complete timeline,
                  insights, and video analysis
                </p>
              </div>
            </div>
          )}

          {/* Tutor Analysis Table */}
          {showTutorAnalysis && teachers.length >= 2 && (
            <div className="mt-8 pt-8 border-t-2 border-blue-200">
              <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <UserCircle className="w-7 h-7 text-[#2f80ed] shrink-0" />
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-800">
                      Analysis by Tutor
                    </h3>
                    <p className="text-sm text-gray-600">
                      Compare performance metrics across all tutors
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    downloadTutorAggregateCsv(
                      processedTutorData,
                      tutorColumnConfigs,
                      `analysis-by-tutor-${institutionId || "export"}`
                    )
                  }
                  disabled={!processedTutorData.length}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-[#2f80ed] border border-[#2f80ed] rounded-lg hover:bg-blue-50 transition-colors shadow-md shrink-0 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              </div>

              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by tutor name..."
                    value={tutorSearchQuery}
                    onChange={(e) => {
                      setTutorSearchQuery(e.target.value);
                      setTutorCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f80ed] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-md">
                <table className="w-full">
                  <thead className="bg-blue-50 border-b border-gray-200">
                    <tr>
                      {/* Tutor Name Column */}
                      <th
                        className="px-4 py-3 text-left cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => handleTutorSort("teacherName")}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700">
                            Tutor Name
                          </span>
                          {tutorSortField === "teacherName" &&
                            (tutorSortDirection === "asc" ? (
                              <ChevronUp className="w-4 h-4 text-[#2f80ed]" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-[#2f80ed]" />
                            ))}
                        </div>
                      </th>

                      {/* Sessions Column */}
                      <th
                        className="px-3 py-3 text-center cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => handleTutorSort("sessionCount")}
                      >
                        <div className="flex flex-col items-center justify-center gap-1">
                          <span className="text-xs font-semibold text-gray-700 leading-tight">
                            Sessions
                          </span>
                          {tutorSortField === "sessionCount" &&
                            (tutorSortDirection === "asc" ? (
                              <ChevronUp className="w-3 h-3 text-[#2f80ed]" />
                            ) : (
                              <ChevronDown className="w-3 h-3 text-[#2f80ed]" />
                            ))}
                        </div>
                      </th>

                      {tutorColumnConfigs.map((cfg) => (
                        <th
                          key={cfg.key}
                          className="px-3 py-3 text-center cursor-pointer hover:bg-blue-100 transition-colors"
                          onClick={() => handleTutorSort(cfg.key)}
                        >
                          <div className="flex flex-col items-center justify-center gap-1">
                            <span className="text-xs font-semibold text-gray-700 leading-tight max-w-[7rem]">
                              {cfg.label}
                            </span>
                            {tutorSortField === cfg.key &&
                              (tutorSortDirection === "asc" ? (
                                <ChevronUp className="w-3 h-3 text-[#2f80ed]" />
                              ) : (
                                <ChevronDown className="w-3 h-3 text-[#2f80ed]" />
                              ))}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedTutorData.length > 0 ? (
                      paginatedTutorData.map((tutor, index) => (
                        <tr
                          key={tutor.teacherId}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <UserCircle className="w-5 h-5 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">
                                {tutor.teacherName}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {tutor.sessionCount}
                            </span>
                          </td>
                          {tutorColumnConfigs.map((cfg) => {
                            const val = tutor[cfg.key];
                            const isTop =
                              val !== null &&
                              val !== undefined &&
                              highestScores[cfg.key] !== null &&
                              val === highestScores[cfg.key];
                            const isLow =
                              val !== null && val !== undefined && val < 60;
                            return (
                              <td
                                key={cfg.key}
                                className="px-4 py-3 text-center"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <span
                                    className={
                                      cfg.key === overallConfig.key
                                        ? "text-sm font-bold"
                                        : "text-sm font-semibold"
                                    }
                                    style={{ color: cfg.color }}
                                  >
                                    {val !== null && val !== undefined
                                      ? val
                                      : "-"}
                                  </span>
                                  {isTop && (
                                    <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                  )}
                                  {isLow && (
                                    <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={2 + tutorColumnConfigs.length}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          {tutorSearchQuery.trim()
                            ? "No tutors found matching your search"
                            : "No tutor data available"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalTutorPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {(tutorCurrentPage - 1) * tutorRowsPerPage + 1} to{" "}
                    {Math.min(
                      tutorCurrentPage * tutorRowsPerPage,
                      processedTutorData.length
                    )}{" "}
                    of {processedTutorData.length} tutors
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTutorCurrentPage(tutorCurrentPage - 1)}
                      disabled={tutorCurrentPage === 1}
                      className={`px-3 py-2 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
                        tutorCurrentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: totalTutorPages },
                        (_, i) => i + 1
                      ).map((page) => {
                        // Show first, last, current, and adjacent pages
                        if (
                          page === 1 ||
                          page === totalTutorPages ||
                          Math.abs(page - tutorCurrentPage) <= 1
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setTutorCurrentPage(page)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                tutorCurrentPage === page
                                  ? "bg-[#2f80ed] text-white"
                                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === tutorCurrentPage - 2 ||
                          page === tutorCurrentPage + 2
                        ) {
                          return (
                            <span key={page} className="px-2 text-gray-400">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setTutorCurrentPage(tutorCurrentPage + 1)}
                      disabled={tutorCurrentPage === totalTutorPages}
                      className={`px-3 py-2 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
                        tutorCurrentPage === totalTutorPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendlineView;
